package pl.decks.personal.components.widgets.responsivegrid

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.breakpoint.ResponsiveValues
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.percent
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGridStylesheet.GridEntry
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGridStylesheet.Vars
import pl.decks.personal.extensions.fromBreakpoint
import pl.decks.personal.extensions.responsiveValue
import kotlin.collections.contains
import kotlin.ranges.until

private class ResponsiveGridSizes(
    chunkNo: Int,
    hoverGrow: Float,
) {
    val base = 100 / chunkNo
    val enlarged = 100 * hoverGrow / chunkNo
    val squished = (100 - enlarged) / (chunkNo - 1)
}

@OptIn(DelicateApi::class)
@Composable
fun ResponsiveGrid(
    options: List<ResponsiveGridOption>,
    selectedOption: Int,
    numColumns: ResponsiveValues<Int>,
    onOptionSelected: (Int) -> Unit,
    onOptionHovered: (Int) -> Unit = { },
    hoverGrow: ResponsiveValues<Float> = responsiveValue(1f),
    modifier: Modifier = Modifier
) {
    var hoveredOption by remember { mutableStateOf<Int?>(null) }
    val breakpoint = rememberBreakpoint()
    val chunkSize = numColumns.fromBreakpoint(breakpoint)
    val optionSizes = ResponsiveGridSizes(chunkSize, hoverGrow.fromBreakpoint(breakpoint))

    Column(
        ResponsiveGridStylesheet.Grid.toModifier()
            .setVariable(Vars.width, optionSizes.base.percent)
            .setVariable(Vars.squishedWidth, optionSizes.squished.percent)
            .setVariable(Vars.enlargedWidth, optionSizes.enlarged.percent)
            .then(modifier)
    ) {
        options.chunked(chunkSize).forEachIndexed { chunkInd, chunk ->
            val chunkRange = chunkInd * chunkSize until (chunkInd + 1) * chunkSize
            val isHoveredInChunk = hoveredOption in chunkRange
            val isSelectedInChunk = selectedOption in chunkRange

            Row(ResponsiveGridStylesheet.GridRow.toModifier()) {
                chunk.forEachIndexed { optionInd, option ->
                    val absoluteIndex = chunkInd * chunkSize + optionInd
                    val isSelected = absoluteIndex == selectedOption
                    val isHovered = absoluteIndex == hoveredOption
                    ResponsiveGridEntry(
                        onClick = { onOptionSelected(absoluteIndex) },
                        variant = when {
                            isHovered -> GridEntry.EnlargedVariant
                            isSelected && !isHoveredInChunk -> GridEntry.EnlargedVariant
                            isHoveredInChunk || isSelectedInChunk -> GridEntry.SquishedVariant
                            else -> GridEntry.EqualedVariant
                        }.then(
                            if (isSelected || isHovered) GridEntry.FocusedInVariant else GridEntry.FocusedOutVariant
                        ),
                        modifier = Modifier
                            .onTouchStart { onOptionHovered(absoluteIndex).also { hoveredOption = absoluteIndex } }
                            .onTouchEnd { hoveredOption = null }
                            .onMouseEnter { onOptionHovered(absoluteIndex).also { hoveredOption = absoluteIndex } }
                            .onMouseLeave { hoveredOption = null },
                    ) {
                        if (option.content != null) option.content()
                        else SpanText(
                            option.title ?: "",
                            modifier = GridEntry.Title.toModifier()
                                .thenIf(isSelected, Modifier.textDecorationLine(TextDecorationLine.Underline))
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ResponsiveGridEntry(
    onClick: () -> Unit,
    variant: CssStyleVariant<GridEntry.Kind>? = null,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) = Box(
    GridEntry.Style.toModifier(variant)
        .onClick { onClick() }
        .cursor(Cursor.Pointer)
        .then(modifier),
) {
    content()
}
